import { Component, ChangeDetectionStrategy, input, output, signal, DestroyRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BlastService } from 'src/app/services/blast.service';
import { DeliveriesService } from 'src/app/services/deliveries.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { DeliveryParams, Operator } from 'src/app/services/hole.service';
import { JsonDelivery, Delivery } from 'src/app/bo/delivery';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-modify-load',
  templateUrl: './modify-load.component.html',
  styleUrls: ['./modify-load.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ModifyLoadComponent {
  form = input.required<FormGroup>();
  deliveryParams = input.required<DeliveryParams>();
  currentDelivery = input.required<JsonDelivery>();
  blastId = input.required<number>();
  currentSiteId = input.required<number>();

  updateDelivery = output<void>();
  moveDelivery = output<number>();
  close = output<void>();
  mergeDelivery = output<{ sourceId: string, destinationId: string }>();

  protected destinationBlast = signal<{ blastId: number, blastName: string }[]>([]);
  protected destBlastId = signal<number | null>(null);
  protected destBlastname = signal<string>('');
  protected destDeliveryName = signal<string>('');
  protected operators = signal<Operator[]>([]);
  protected managers = signal<Operator[]>([]);
  protected availableDeliveries = signal<Delivery[]>([]);
  protected selectedMergeDelivery = signal<Delivery | null>(null);
  protected showMergeConfirmation = signal<boolean>(false);

  inputBlast = signal<string>('');
  wrongSiteAlerte = signal<string>('');

  constructor(
    private readonly blastService: BlastService,
    private readonly deliveriesService: DeliveriesService,
    private readonly fonctionsService: FonctionsService,
    private readonly destroyRef: DestroyRef
  ) {
  }

  
  ngOnInit() {
    this.managers.set(this.deliveryParams().operators.filter((op) =>
      op.roles.includes('BLASTER')));
    this.operators.set(this.deliveryParams().operators.filter((op) =>
      op.roles.includes('OPERATOR')));
    
    if (this.blastId() && this.currentDelivery()) {
      this.loadAvailableDeliveries();
    }
  }

  getSelectedOperatorIds(): number[] {
    const selectedOperators = this.form().get('operators')?.value;
    return selectedOperators || [];
  }

  getSelectedOperatorNames(): string[] {
    const selectedIds = this.getSelectedOperatorIds();
    return this.operators()
      .filter(op => selectedIds.includes(op.operatorId))
      .map(op => op.nom);
  }

  onSubmit() {
    this.updateDelivery.emit();
  }

  onClose() {
    this.close.emit();
  }

  protected onDestBlastIdChange(blastId: number) {
    if (blastId) {
      this.blastService.getBlast(blastId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
        if (this.currentSiteId() == res.siteId) {
          this.wrongSiteAlerte.set('');
        }
        else {
          this.wrongSiteAlerte.set('Wrong site');
        }
      },
        (error) => {
          this.wrongSiteAlerte.set('Not found');
        });
    }


  }

  protected onMoveDelivery(destBlastId: number): void {
    if (this.form().get('destBlastId').value == null) {
      this.wrongSiteAlerte.set('Please select a destination blast');
      return;
    }

    this.moveDelivery.emit(destBlastId);
  }

  // Conversion methods for hole diameter
  getHoleDiameterInMm(): number {
    const diameterInM = this.form().get('holeDiameter')?.value;
    if (diameterInM != null && diameterInM !== '') {
      return Math.round(diameterInM * 1000);
    }
    return null;
  }

  setHoleDiameterFromMm(event: any): void {
    const diameterInMm = parseFloat(event.target.value);
    if (!isNaN(diameterInMm)) {
      const diameterInM = diameterInMm / 1000;
      this.form().get('holeDiameter')?.setValue(diameterInM);
    } else {
      this.form().get('holeDiameter')?.setValue(null);
    }
  }

  private loadAvailableDeliveries(): void {
    if (this.blastId() && this.currentDelivery()) {
      this.deliveriesService.getDeliveriesByTirId(this.blastId())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
        next: (deliveries: Delivery[]) => {
          this.availableDeliveries.set(deliveries.filter(d => d.deliveryId !== this.currentDelivery().guid));
        },
        error: (error) => {
          this.availableDeliveries.set([]);
        }
      });
    } 
  }

  onMergeDelivery(): void {
    if (this.selectedMergeDelivery() && this.currentDelivery()) {
      this.showMergeConfirmation.set(true);
    }
  }

  onMergeDeliveryChange(delivery: Delivery): void {
    this.selectedMergeDelivery.set(delivery);
  }

  confirmMergeDelivery(): void {
    if (this.selectedMergeDelivery() && this.currentDelivery()) {
      this.deliveriesService.MergeDeliveries(this.currentDelivery().guid, this.selectedMergeDelivery().deliveryId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.fonctionsService.launch_toast(true);
            this.mergeDelivery.emit({
              sourceId: this.currentDelivery().guid,
              destinationId: this.selectedMergeDelivery().deliveryId
            });
            this.showMergeConfirmation.set(false);
            this.selectedMergeDelivery.set(null);
          },
          error: (error) => {
            this.fonctionsService.launch_toast(false);
            this.showMergeConfirmation.set(false);
          }
        });
    }
  }

  cancelMergeDelivery(): void {
    this.showMergeConfirmation.set(false);
    this.selectedMergeDelivery.set(null);
  }
}
